import { type PaletteMode, type PaletteOptions } from "@mui/material/styles";
/**
 * Crea la paleta de colores según el modo (light/dark)
 */
export function createPalette(mode: PaletteMode): PaletteOptions {
  const isLight = mode === "light";
  return {
    mode,
    // Colores de Marca (mantienen consistencia en ambos modos)
    primary: {
      main: "#00A859", // Verde primario Némesis
      light: isLight ? "#E6F6EE" : "#004d2a",
      dark: "#008546",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: isLight ? "#1D293D" : "#475569",
      light: "#475569",
      dark: "#0f172a",
      contrastText: "#FFFFFF",
    },
    // Estados y Acciones
    success: {
      main: "#00A859",
      light: isLight ? "#E6F6EE" : "#004d2a",
      dark: "#008546",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#EF4444",
      light: isLight ? "#FEE2E2" : "#7f1d1d",
      dark: "#DC2626",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#F59E0B",
      light: isLight ? "#FEF3C7" : "#78350f",
      dark: "#D97706",
      contrastText: isLight ? "#1D293D" : "#FFFFFF",
    },
    info: {
      main: "#0EA5E9",
      light: isLight ? "#E0F2FE" : "#075985",
      dark: "#0284C7",
      contrastText: "#FFFFFF",
    },
    // Escala de Grises adaptativa
    grey: isLight
      ? {
          50: "#F8FAFC",
          100: "#F3F4F6",
          200: "#E5E7EB",
          300: "#CBD5E1",
          400: "#94A3B8",
          500: "#64748b",
          600: "#475569",
          700: "#334155",
          800: "#1D293D",
          900: "#0f172a",
        }
      : {
          50: "#0f172a", // Invertido para dark
          100: "#1e293b",
          200: "#293548",
          300: "#334155",
          400: "#475569",
          500: "#64748b",
          600: "#94A3B8",
          700: "#CBD5E1",
          800: "#E5E7EB",
          900: "#F8FAFC",
        },
    // Fondos adaptativos
    background: {
      default: isLight ? "#F8FAFC" : "#0f172a",
      paper: isLight ? "#FFFFFF" : "#1e293b",
    },
    // Textos adaptativos
    text: {
      primary: isLight ? "#1D293D" : "#F8FAFC",
      secondary: isLight ? "#475569" : "#94A3B8",
      disabled: isLight ? "#94A3B8" : "#64748b",
    },
    // Divisores adaptativos
    divider: isLight ? "#CBD5E1" : "#334155",
    // Acciones
    action: {
      active: isLight ? "rgba(0, 0, 0, 0.54)" : "rgba(255, 255, 255, 0.7)",
      hover: isLight ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
      selected: isLight ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.16)",
      disabled: isLight ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
      disabledBackground: isLight
        ? "rgba(0, 0, 0, 0.12)"
        : "rgba(255, 255, 255, 0.12)",
    },
  };
}
/**
 * Colores para nomencladores (adaptados según el modo)
 */
export const NOMENCLATOR_COLORS = {
  greenPrimary: {
    light: { value: "#E6F6EE", dark: "#00A859" },
    dark: { value: "#064e3b", dark: "#10b981" },
  },
  blueInfo: {
    light: { value: "#E0F2FE", dark: "#0EA5E9" },
    dark: { value: "#075985", dark: "#38bdf8" },
  },
  amber: {
    light: { value: "#FEF3C7", dark: "#F59E0B" },
    dark: { value: "#78350f", dark: "#fbbf24" },
  },
  red: {
    light: { value: "#FEE2E2", dark: "#EF4444" },
    dark: { value: "#7f1d1d", dark: "#f87171" },
  },
  grayLight: {
    light: { value: "#F3F4F6", dark: "#6B7280" },
    dark: { value: "#374151", dark: "#9ca3af" },
  },
  purple: {
    light: { value: "#F3E8FF", dark: "#A855F7" },
    dark: { value: "#581c87", dark: "#c084fc" },
  },
  indigo: {
    light: { value: "#E0E7FF", dark: "#6366F1" },
    dark: { value: "#3730a3", dark: "#818cf8" },
  },
  pink: {
    light: { value: "#FCE7F3", dark: "#EC4899" },
    dark: { value: "#831843", dark: "#f472b6" },
  },
  teal: {
    light: { value: "#CCFBF1", dark: "#14B8A6" },
    dark: { value: "#0f766e", dark: "#5eead4" },
  },
  lime: {
    light: { value: "#ECFCCB", dark: "#84CC16" },
    dark: { value: "#3f6212", dark: "#a3e635" },
  },
  orange: {
    light: { value: "#FED7AA", dark: "#F97316" },
    dark: { value: "#7c2d12", dark: "#fb923c" },
  },
  grayDark: {
    light: { value: "#6B7280", dark: "#1F2937" },
    dark: { value: "#1f2937", dark: "#d1d5db" },
  },
} as const;
export type NomenclatorColorKey = keyof typeof NOMENCLATOR_COLORS;
/**
 * Obtiene el color de nomenclador según el modo
 */
export function getNomenclatorColor(
  colorKey: NomenclatorColorKey,
  mode: PaletteMode
): { value: string; dark: string; label: string } {
  const colors = NOMENCLATOR_COLORS[colorKey];
  const modeColors = colors[mode];
  // Mapeo de nombres en español
  const labels: Record<NomenclatorColorKey, string> = {
    greenPrimary: "Verde Primario",
    blueInfo: "Azul Info",
    amber: "Ámbar",
    red: "Rojo",
    grayLight: "Gris Claro",
    purple: "Púrpura",
    indigo: "Índigo",
    pink: "Rosa",
    teal: "Teal",
    lime: "Lime",
    orange: "Naranja",
    grayDark: "Gris Oscuro",
  };
  return {
    value: modeColors.value,
    dark: modeColors.dark,
    label: labels[colorKey],
  };
}
/**
 * Obtiene todos los colores de nomencladores para el modo actual
 */
export function getAllNomenclatorColors(mode: PaletteMode) {
  return Object.keys(NOMENCLATOR_COLORS).map((key) =>
    getNomenclatorColor(key as NomenclatorColorKey, mode)
  );
}
